import {
  Control,
  Controller,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';

import {
  getDecimalJSSupportingOnChange,
  getFieldErrorValue,
  getPercentMaxValidation,
  getPossiblyDecimalFieldValue,
  getValidations,
} from '@/components/utils/inputUtils';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import { HelpTextVariant } from '../baseInputs/inputTypes';
import { PercentInput } from '../baseInputs/PercentInput';
import { useFormFieldsDisabled } from '../context/formFieldsDisabled.context';
import { useValidateOnChange } from './hooks/useValidateOnChange';

export interface FormAwarePercentInputProps<FormShape extends FieldValues> {
  fieldName: FieldNameFromFormShape<FormShape>;
  label: string;
  control: Control<FormShape>;
  onFocus?: () => void;
  disabled?: boolean;
  validation?: RegisterOptions<FormShape>['validate'];
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  required?: boolean;
  decimalScale?: number;
  maxValue?: number;
  fixedDecimalScale?: boolean;
  allowNegative?: boolean;
  contextualHelp?: JSX.Element;
  isDecimalJSInput?: boolean;
  hideLabel?: boolean;
  validateOnChange?: boolean;
  endAdornment?: React.ReactNode;
  placeholder?: string;
}

export function FormAwarePercentInput<FormShape extends FieldValues>({
  fieldName,
  label,
  helpText,
  helpTextVariant,
  required,
  control,
  onFocus,
  disabled,
  validation,
  decimalScale = 2,
  fixedDecimalScale,
  allowNegative = false,
  maxValue = 100,
  isDecimalJSInput,
  contextualHelp,
  hideLabel,
  validateOnChange,
  endAdornment,
  placeholder,
}: FormAwarePercentInputProps<FormShape>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const validations = getValidations(label, !!required, {
    ...validation,
    maxValue: getPercentMaxValidation({
      disabled,
      isDecimalJSInput,
      label,
      maxValue,
    }),
  });

  useValidateOnChange<FormShape>(
    validateOnChange ?? false,
    fieldName,
    control,
    { valueType: 'decimal' }
  );

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ validate: validations }}
      render={({ field, fieldState, formState }) => {
        return (
          <PercentInput
            label={label}
            onChange={getDecimalJSSupportingOnChange(field.onChange, {
              isDecimalJS: !!isDecimalJSInput,
            })}
            value={getPossiblyDecimalFieldValue(field.value, {
              isDecimalJS: !!isDecimalJSInput,
              fieldName: field.name,
              decimalScale,
              fixedDecimalScale,
            })}
            onBlur={field.onBlur}
            onFocus={onFocus}
            name={field.name}
            inputRef={field.ref}
            required={required}
            helpText={helpText}
            helpTextVariant={helpTextVariant}
            disabled={disabledFromContext ?? disabled}
            decimalScale={decimalScale}
            fixedDecimalScale={fixedDecimalScale}
            allowNegative={allowNegative}
            errorMessage={getFieldErrorValue(
              fieldState,
              formState.isSubmitted,
              validateOnChange ?? false
            )}
            contextualHelp={contextualHelp}
            hideLabel={hideLabel}
            endAdornment={endAdornment}
            placeholder={placeholder}
          />
        );
      }}
    />
  );
}
